import { useEffect, useState } from "react";
import type { Game } from "@/mocks/games";
import { getCategoryName } from "@/utils/gameUtils";

interface PlayModalProps {
  game: Game | null;
  onClose: () => void;
}

export default function PlayModal({ game, onClose }: PlayModalProps) {
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!game) return;
    setProgress(0);
    setReady(false);
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        const next = prev + Math.round(Math.random() * 16 + 6);
        if (next >= 100) {
          window.clearInterval(timer);
          setReady(true);
          return 100;
        }
        return next;
      });
    }, 240);
    return () => window.clearInterval(timer);
  }, [game]);

  useEffect(() => {
    if (!game) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [game, onClose]);

  if (!game) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/80 p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${game.name} 试玩窗口`}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-lg bg-foreground-950 text-background-50 animate-pop"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative h-44 md:h-56 w-full overflow-hidden">
          <img
            src={game.cover}
            alt={`${game.name} 试玩加载画面`}
            className="h-full w-full object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/50 to-transparent" />
          <div className="absolute bottom-4 left-4 md:left-6 right-16">
            <span className="inline-block rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
              {getCategoryName(game.category)}
            </span>
            <h2 className="mt-2 text-xl md:text-2xl font-black text-background-50">{game.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭试玩窗口"
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950/60 text-background-50 transition hover:bg-primary-500"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-4 py-5 md:px-6 md:py-6">
          {!ready ? (
            <div className="py-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-500/20 text-primary-400 animate-float">
                <i className="ri-loader-4-line text-3xl"></i>
              </div>
              <p className="mt-4 text-sm text-background-300">正在加载游戏资源…</p>
              <div className="mx-auto mt-4 h-2 w-full max-w-md overflow-hidden rounded-full bg-foreground-800">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-background-400">{progress}%</p>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-foreground-700 bg-foreground-900/60 p-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-secondary-500/20 text-secondary-400">
                <i className="ri-play-circle-line text-3xl"></i>
              </div>
              <h3 className="mt-3 text-lg font-bold text-background-50">演示区域</h3>
              <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-background-300">
                {game.name} 的试玩窗口已就绪。此窗口为前端演示，用于模拟游戏启动界面，
                <span className="text-primary-400">暂不具备真实游戏运行能力</span>，后续接入真实游戏资源后即可直接开玩。
              </p>
              <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setReady(false);
                    setProgress(0);
                    const timer = window.setInterval(() => {
                      setProgress((prev) => {
                        const next = prev + Math.round(Math.random() * 16 + 6);
                        if (next >= 100) {
                          window.clearInterval(timer);
                          setReady(true);
                          return 100;
                        }
                        return next;
                      });
                    }, 240);
                  }}
                  className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-foreground-800 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-foreground-700"
                >
                  <i className="ri-refresh-line"></i>
                  重新加载
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
                >
                  <i className="ri-check-line"></i>
                  我知道了
                </button>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-start gap-2 rounded-md bg-foreground-900/60 px-3 py-2.5 text-xs text-background-300">
            <i className="ri-information-line mt-0.5 text-primary-400"></i>
            <span>温馨提示：本试玩为前端演示效果，无需下载安装；正式游戏需接入对应游戏运行环境。</span>
          </div>
        </div>
      </div>
    </div>
  );
}